import { Link } from 'react-router-dom';
import { Anchor, Stack, Text, Title } from '@mantine/core';
import { Breadcrumbs } from '@components';
import { sellerHelpUrl } from '@shared/utils/seller-help-url';
import '@shared/styles/page.css';
import './SellerOnboardingPage.css';

type OnboardingShot = {
  src: string;
  alt: string;
  caption: string;
};

type OnboardingStep = {
  n: number;
  title: string;
  lead: string;
  bullets: string[];
  links: { to: string; label: string }[];
  shots?: OnboardingShot[];
};

/** public/ assets: respect Vite base. */
const onboardingAsset = (file: string) =>
  `${import.meta.env.BASE_URL}onboarding-assets/${file}`;

const STEPS: OnboardingStep[] = [
  {
    n: 1,
    title: 'Точки самовывоза',
    lead:
      'Начните с точек — без них нельзя создать товар и указать остатки. Покупатели видят ваш магазин в каталоге города только если есть точка самовывоза в этом городе.',
    bullets: [
      'Откройте «Точки самовывоза» в меню слева и нажмите «+», чтобы добавить адрес, телефон и контактный email.',
      'В карточке точки заполните вкладки: контакты, доставка (если везёте сами — зоны на карте), расписание работы и слоты выдачи.',
      'Без рабочего расписания точка не сможет принимать заказы — кабинет покажет предупреждение «Магазин не работает».',
    ],
    links: [{ to: '/contacts', label: 'Точки самовывоза' }],
    shots: [
      {
        src: onboardingAsset('pickup-points.png'),
        alt: 'Список точек самовывоза в кабинете',
        caption: 'Раздел «Точки самовывоза» — список адресов и кнопка «+» для новой точки',
      },
      {
        src: onboardingAsset('pickup-points-details.png'),
        alt: 'Расписание и вкладки точки самовывоза',
        caption: 'В карточке точки: контакты, доставка, расписание, слоты и остатки',
      },
    ],
  },
  {
    n: 2,
    title: 'Товары и каталог',
    lead:
      'Когда есть хотя бы одна точка, можно наполнять витрину. Кнопка создания товара активна только при наличии точки самовывоза.',
    bullets: [
      'В разделе «Товары» создайте карточку: название, фото, описание, цена, состав и категории.',
      'После сохранения укажите остатки по точкам — иначе покупатели не смогут оформить заказ.',
      'Дождитесь модерации карточки.',
      'Поздравляем — после одобрения товар виден покупателям в приложении.',
    ],
    links: [{ to: '/products', label: 'Товары' }],
    shots: [
      {
        src: onboardingAsset('products.png'),
        alt: 'Список товаров в кабинете',
        caption: 'Раздел «Товары» — карточки витрины и создание новой позиции',
      },
    ],
  },
  {
    n: 3,
    title: 'Заказы',
    lead: 'Когда появятся покупатели, следите за входящими заказами в разделе «Заказы».',
    bullets: [
      'В «Заказах» видны новые и текущие заказы — от оплаты до выдачи или доставки.',
      'Новый заказ нужно взять в работу в течение ~10 минут — иначе он отменится автоматически, и к магазину могут применить санкции.',
      'Когда букет собран, нажмите «Готов» и приложите фото готового букета.',
      'Если доставляете сами — двигайте заказ по статусам вручную: «Передать в доставку», а после получения клиентом — «Доставлен».',
      'Если передаёте заказ курьеру нашего партнёра — статусы обновятся автоматически.',
    ],
    links: [{ to: '/orders', label: 'Заказы' }],
    shots: [
      {
        src: onboardingAsset('orders.png'),
        alt: 'Список заказов в кабинете',
        caption: 'Раздел «Заказы» — статусы и список входящих заказов',
      },
    ],
  },
  {
    n: 4,
    title: 'Бонусы, допы, промокоды и продвижение',
    lead: 'Это необязательно для старта — можно настроить позже, когда витрина и заказы уже работают.',
    bullets: [
      'Бонусная система — правила начисления и списания баллов для покупателей.',
      'Допы — дополнительные позиции к букету (открытки, вазы и т.п.). Настраиваются в карточке товара.',
      'Промокоды — скидки для акций и повторных покупок.',
      'Продвижение — ставка за клик, чтобы товары выше поднимались в каталоге.',
    ],
    links: [
      { to: '/bonuses', label: 'Бонусная система' },
      // { to: '/extras', label: 'Допы' },
      { to: '/promo-codes', label: 'Промокоды' },
      { to: '/promotion', label: 'Продвижение' },
    ],
  },
  {
    n: 5,
    title: 'Доступы для сотрудников',
    lead: 'Если в магазине работают несколько человек, можно выдать им вход в кабинет без доступа ко всем разделам владельца.',
    bullets: [
      'В «Доступах» добавьте сотрудника по телефону и/или email и выберите разделы, которыми он может пользоваться.',
      'Финансы, юридические данные и список доступов остаются только у владельца.',
    ],
    links: [{ to: '/accesses', label: 'Доступы' }],
  },
];

/** Подсказки после одобрения заявки: куда зайти в кабинете в первую очередь. */
export default function SellerOnboardingPage() {
  return (
    <div className="page-container seller-onboarding-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Первые шаги' },
        ]}
      />
      <div className="page-header">
        <h1>Первые шаги после одобрения</h1>
      </div>
      <div className="content-wrapper">
        <Stack gap="xl" className="seller-onboarding-body">
          <Text size="sm" c="dimmed" maw={720} style={{ lineHeight: 1.55 }}>
            Заявка принята — можно настраивать точки, наполнять витрину и принимать заказы. Ниже порядок, с которым
            удобнее всего стартовать: сначала точки самовывоза, затем товары.
          </Text>

          <ol className="seller-onboarding-steps">
            {STEPS.map((step) => (
              <li key={step.n} className="seller-onboarding-step">
                <div className="seller-onboarding-step-head">
                  <span className="seller-onboarding-step-num" aria-hidden>
                    {step.n}
                  </span>
                  <div>
                    <Title order={3} className="seller-onboarding-step-title">
                      {step.title}
                    </Title>
                    <Text size="sm" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>
                      {step.lead}
                    </Text>
                  </div>
                </div>

                <ul className="seller-onboarding-bullets">
                  {step.bullets.map((b) => (
                    <li key={b}>
                      <Text size="sm" style={{ lineHeight: 1.55 }}>
                        {b}
                      </Text>
                    </li>
                  ))}
                </ul>

                <div className="seller-onboarding-open">
                  {step.links.map((link) => (
                    <Link key={link.to} to={link.to} className="seller-onboarding-open-btn">
                      {link.label}
                    </Link>
                  ))}
                </div>

                {step.shots && step.shots.length > 0 ? (
                  <div
                    className={
                      step.shots.length > 1
                        ? 'seller-onboarding-shots seller-onboarding-shots--pair'
                        : 'seller-onboarding-shots'
                    }
                  >
                    {step.shots.map((shot) => (
                      <figure key={shot.src} className="seller-onboarding-figure">
                        <img src={shot.src} alt={shot.alt} loading="lazy" />
                        <figcaption>{shot.caption}</figcaption>
                      </figure>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>

          <section className="seller-onboarding-step seller-onboarding-step--extra">
            <div className="seller-onboarding-step-head">
              <span className="seller-onboarding-step-num" aria-hidden>
                ✓
              </span>
              <div>
                <Title order={3} className="seller-onboarding-step-title">
                  Сводка на главной
                </Title>
                <Text size="sm" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>
                  На{' '}
                  <Anchor component={Link} to="/" className="seller-onboarding-link">
                    главной
                  </Anchor>{' '}
                  кабинета видна динамика продаж — удобно отслеживать запуск после первых заказов.
                </Text>
              </div>
            </div>
            <figure className="seller-onboarding-figure">
              <img
                src={onboardingAsset('main.png')}
                alt="Главная страница кабинета со сводкой продаж"
                loading="lazy"
              />
              <figcaption>Главная — сводка и график продаж за выбранный период</figcaption>
            </figure>
          </section>

          <section className="seller-onboarding-step seller-onboarding-step--extra">
            <div className="seller-onboarding-step-head">
              <span className="seller-onboarding-step-num" aria-hidden>
                ✓
              </span>
              <div>
                <Title order={3} className="seller-onboarding-step-title">
                  Финансы
                </Title>
                <Text size="sm" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>
                  В разделе{' '}
                  <Anchor component={Link} to="/finance" className="seller-onboarding-link">
                    Финансы
                  </Anchor>{' '}
                  можно следить за балансом, суммой к выплате, историей начислений и документами по выплатам.
                </Text>
              </div>
            </div>
            <figure className="seller-onboarding-figure">
              <img
                src={onboardingAsset('finance.png')}
                alt="Страница финансов в кабинете"
                loading="lazy"
              />
              <figcaption>Финансы — баланс, выплаты и история начислений</figcaption>
            </figure>
          </section>

          <section className="seller-onboarding-step seller-onboarding-step--extra">
            <div className="seller-onboarding-step-head">
              <span className="seller-onboarding-step-num" aria-hidden>
                ✓
              </span>
              <div>
                <Title order={3} className="seller-onboarding-step-title">
                  Помощь и поддержка
                </Title>
                <Text size="sm" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>
                  Ответы на частые вопросы — в разделе{' '}
                  <Anchor
                    href={sellerHelpUrl()}
                    className="seller-onboarding-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Помощь
                  </Anchor>
                  . Если не нашли нужное — напишите нам в{' '}
                  <Anchor component={Link} to="/support-chat" className="seller-onboarding-link">
                    чат с поддержкой
                  </Anchor>
                  .
                </Text>
              </div>
            </div>
          </section>
        </Stack>
      </div>
    </div>
  );
}
